import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RuleLogicChips } from '@/components/recommendations/ruleTables/RuleLogicChips';
import { RuleLogicEditor } from '@/components/recommendations/ruleTables/RuleLogicEditor';
import { RecommendationTableToolbar } from '@/components/recommendations/RecommendationTableToolbar';
import type { RecommendationSortField, RecommendationSortOrder } from '@/api/recommendationUtils';
import { ruleTableSearchPlaceholder } from '@/api/ruleRecommendations';
import type { RuleLogicChip, RuleTableId, RuleThresholds } from '@/types/ruleRecommendations';

export function RuleTableCard({
  tableId,
  title,
  count,
  chips,
  rangeLabel,
  note,
  thresholds,
  onThresholdsChange,
  onResetThresholds,
  searchQuery,
  onSearchChange,
  sortBy,
  sortOrder,
  onSortByChange,
  onSortOrderChange,
  showingCount,
  totalCount,
  defaultExpanded,
  children,
}: {
  tableId: RuleTableId;
  title: string;
  count: number;
  chips: RuleLogicChip[];
  rangeLabel: string;
  note?: string;
  thresholds: RuleThresholds;
  onThresholdsChange: (next: RuleThresholds) => void;
  onResetThresholds: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortBy: RecommendationSortField;
  sortOrder: RecommendationSortOrder;
  onSortByChange: (value: RecommendationSortField) => void;
  onSortOrderChange: (value: RecommendationSortOrder) => void;
  showingCount: number;
  totalCount: number;
  defaultExpanded: boolean;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface">
      <button
        type="button"
        className="flex w-full items-center gap-2 border-b border-border bg-page px-4 py-3 text-left"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        <Chevron className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden />
        <h3 className="min-w-0 flex-1 font-display text-headline-md text-foreground">{title}</h3>
        <span className="font-mono text-body-sm text-foreground-subtle">{count} rows</span>
      </button>

      {expanded ? (
        <>
          <div className="flex flex-wrap items-start gap-3 border-b border-border px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="mb-1.5 text-xs font-medium uppercase tracking-[0.08em] text-foreground-subtle">
                Rules
              </p>
              <RuleLogicChips chips={chips} rangeLabel={rangeLabel} />
              {note ? <p className="mt-2 text-xs text-foreground-muted">{note}</p> : null}
            </div>
            <RuleLogicEditor
              tableId={tableId}
              thresholds={thresholds}
              onChange={onThresholdsChange}
              onReset={onResetThresholds}
            />
          </div>

          <RecommendationTableToolbar
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            actionFilter="ALL"
            onActionFilterChange={() => {}}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortByChange={onSortByChange}
            onSortOrderChange={onSortOrderChange}
            showingCount={showingCount}
            totalCount={totalCount}
            searchPlaceholder={ruleTableSearchPlaceholder(tableId)}
          />

          {children}
        </>
      ) : null}
    </section>
  );
}
